import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '../../utils/api';

const ADMIN = '/admin';

function normalizeBookingsList(payload) {
  if (payload == null) return [];
  const raw = payload?.data ?? payload;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.bookings)) return raw.bookings;
  return [];
}

// GET /admin/get-all-bookings
export const fetchBookings = createAsyncThunk(
  'booking/fetchBookings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-all-bookings`, { params });
      return normalizeBookingsList(data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch bookings');
    }
  }
);

// GET /admin/get-booking/:id
export const fetchBookingById = createAsyncThunk(
  'booking/fetchBookingById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-booking/${id}`);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch booking');
    }
  }
);

// GET /admin/get-bookings-by-salon/:salonId
export const fetchBookingsBySalon = createAsyncThunk(
  'booking/fetchBookingsBySalon',
  async (salonId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-bookings-by-salon/${salonId}`);
      return normalizeBookingsList(data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch salon bookings');
    }
  }
);

// PUT /admin/update-booking-status/:id
export const updateBookingStatus = createAsyncThunk(
  'booking/updateBookingStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`${ADMIN}/update-booking-status/${id}`, { status });
      return data?.data ?? data ?? { id, status };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to update booking status');
    }
  }
);

const initialState = {
  bookings: [],
  selectedBooking: null,
  loading: false,
  error: null,
};

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    selectBooking: (state, action) => {
      state.selectedBooking = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload ?? [];
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch bookings';
      })
      .addCase(fetchBookingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBooking = action.payload ?? null;
        const b = action.payload;
        if (b?.id) {
          const i = state.bookings.findIndex((x) => x.id === b.id);
          if (i !== -1) state.bookings[i] = { ...state.bookings[i], ...b };
        }
      })
      .addCase(fetchBookingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch booking';
      })
      .addCase(fetchBookingsBySalon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookingsBySalon.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload ?? [];
      })
      .addCase(fetchBookingsBySalon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch salon bookings';
      })
      .addCase(updateBookingStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBookingStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        if (updated?.id != null) {
          const i = state.bookings.findIndex((a) => a.id === updated.id);
          if (i !== -1) {
            state.bookings[i] = { ...state.bookings[i], ...updated };
          }
        }
      })
      .addCase(updateBookingStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update booking status';
      });
  },
});

export const { selectBooking, clearError } = bookingSlice.actions;
export default bookingSlice.reducer;

export const selectBookings = (state) => state.booking.bookings;
export const selectSelectedBooking = (state) => state.booking.selectedBooking;
export const selectBookingLoading = (state) => state.booking.loading;
export const selectBookingError = (state) => state.booking.error;

export function useBooking() {
  const dispatch = useDispatch();
  const bookings = useSelector(selectBookings);
  const selectedBooking = useSelector(selectSelectedBooking);
  const loading = useSelector(selectBookingLoading);
  const error = useSelector(selectBookingError);

  return {
    bookings,
    selectedBooking,
    loading,
    error,
    fetchBookings: useCallback((params) => dispatch(fetchBookings(params)), [dispatch]),
    fetchBookingById: useCallback((id) => dispatch(fetchBookingById(id)), [dispatch]),
    fetchBookingsBySalon: useCallback((salonId) => dispatch(fetchBookingsBySalon(salonId)), [dispatch]),
    updateBookingStatus: useCallback((payload) => dispatch(updateBookingStatus(payload)), [dispatch]),
  };
}
